import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useEOSAuth } from "../../contexts/EOSAuthContext";
import { useAccount } from "../../contexts/AccountContext";

export function useUserDashboard() {
  const { isAuthenticated } = useEOSAuth();
  const { account, holdings, loading, error, refreshData } = useAccount();
  const location = useLocation();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (location.pathname === "/account-details" && isAuthenticated) {
      refreshData(true);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [location.pathname, isAuthenticated, refreshData]);

  const handleRefresh = () => {
    refreshData(true);
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getPnLColor = (pnl?: number) => {
    if (!pnl) return "text-slate-500";
    return pnl >= 0 ? "text-green-300" : "text-red-300";
  };

  const getPnLDirection = (pnl?: number): "up" | "down" | "neutral" => {
    if (!pnl) return "neutral";
    return pnl >= 0 ? "up" : "down";
  };

  const getPnLPercentage = (pnl?: number, avgPrice?: number, quantity?: number) => {
    if (!pnl || !avgPrice || !quantity) return "0.00%";
    const totalInvestment = avgPrice * quantity;
    if (totalInvestment === 0) return "0.00%";
    const percentage = (pnl / totalInvestment) * 100;
    return `${percentage >= 0 ? "+" : ""}${percentage.toFixed(2)}%`;
  };

  const totalInvested = holdings.reduce((sum, h) => sum + (h.average_price ?? 0) * (h.quantity ?? 0), 0);
  const currentValue = holdings.reduce((sum, h) => sum + (h.last_price ?? 0) * (h.quantity ?? 0), 0);
  const totalProfit = currentValue - totalInvested;
  const totalProfitPercent = totalInvested > 0 ? (totalProfit / totalInvested) * 100 : 0;

  return {
    isAuthenticated,
    account,
    holdings,
    loading,
    error,
    refreshData,
    handleRefresh,
    formatCurrency,
    getPnLColor,
    getPnLDirection,
    getPnLPercentage,
    totalInvested,
    currentValue,
    totalProfit,
    totalProfitPercent,
  };
}
